(ns ycweb.util.markdown.parse
  (:require
    [util.string :as str]
    [ycweb.util.core :as u]))

(defn wrap-tags [[t & ts] content]
  (cond
    (empty? content) nil
    (nil? t) content
    :else [(into [t] (wrap-tags ts content))]))

(defn collect [acc]
  (->
    acc
    (update :out into
      (wrap-tags
        (:open-tags acc)
        (conj (:collect-tags acc) (:collected acc))))

    (assoc :collected "" :collect-tags [])))

(defn handle-tag [acc tag]
  (if (contains? (:open-tags acc) tag)
    ;; Closing the tag, collect.
    (->
      (collect acc)
      (update :open-tags disj tag))

    ;; Opening the tag, start collecting.
    (->
      (collect acc)
      (update :open-tags conj tag))))

(defn trf-link [url]
  (cond
    (or
      (str/starts-with? url "http://")
      (str/starts-with? url "https://")) url
    (str/index-of url "@") (str "mailto:" url)
    :else url))

(defn parse-link [cs]
  (let [[m text _ url _ alt]
        (re-find #"([^\]]+)[\]][\(](([^\"\s)]+)(\s*[\"]([^\"]+)[\"])?[\)])"
          (apply str cs))]
    (when m
      {:url (trf-link url)
       :length (count m)
       :text text
       :alt alt})))

(defn parse-simple-link [cs]
  (let [[link-full link] (re-find #"([^>]*)[>]" (apply str cs))]
    {:url (trf-link link)
     :length (count link-full)
     :text link}))

(defn parse-image [cs]
  (let [link (parse-link (rest cs))]
    (when link
      (->
        link
        (assoc :img true)
        (update :length inc)))))

(defn parse-line
  "Basic inline formatting - images, links, italics, bold - that kind of thing"
  [line]
  (loop [[c & cs] line
         acc {:out [] :open-tags #{} :collect-tags [] :collected ""}]
    (cond
      ;; Finished? great!
      (nil? c) (remove nil? (:out (collect acc)))

      ;; Escape the next character.
      (= c \\)
      (recur
        (rest cs)
        (update acc :collected #(str % (first cs))))

      ;; Code mode? sluuurp moore
      (and
        (not= \` c)
        (contains? (:open-tags acc) :code))
      (recur
        cs
        (update acc :collected #(str % c)))

      ;; Check for ridiculously complicated image/link syntax
      (or
        (#{\< \[} c)
        (and (= \! c) (#{\< \[} (first cs))))
      (let [link
            (cond
              (= \! c) (parse-image cs)
              (and (= \< c) (not= \! (first cs))) (parse-simple-link cs)
              :else (parse-link cs))]
        (cond
          link
          (recur
            (drop (:length link) cs)
            (->
              acc
              (update :collect-tags conj (:collected acc))
              (update :collect-tags conj
                (if (:img link)
                  [:img {:src (:url link) :title (:text link)}]
                  (into
                    [:a {:href (:url link) :alt (:alt link)}]
                    (parse-line (:text link)))))
              (assoc :collected "")))

          :else
          (recur cs
            (update acc :collected #(str % c)))))

      ;; Check for over-embolden
      (or
        (and (= \* c) (= \* (first cs)))
        (and (= \_ c) (= \_ (first cs)))
        (and (= \_ c) (= \* (first cs)))
        (and (= \* c) (= \_ (first cs))))
      (recur
        (rest cs)
        (handle-tag acc :strong))

      ;; Bold 'tags'
      (and (= \* c))
      (recur cs (handle-tag acc :b))

      ;; Italics 'tags'
      (and (= \_ c))
      (recur cs (handle-tag acc :i))

      ;; Code begins.
      (and (= \` c))
      (recur cs (handle-tag acc :code))

      ;; Slurp.
      :else
      (recur
        cs
        (update acc :collected #(str % c))))))

(defn annotate-line-type [l]
  (let [[_ spacing _ h ol ul q code remaining :as m]
        (re-find #"(\s*)(([#]+)|([0-9]+\s?[.])|([*-])|([>])|([`]{3}.*))?(.*)" l)

        spacing (str/replace spacing "\t" "    ")

        tag
        (when m
          (cond
            h (keyword (str "h" (count h)))
            ol :ol
            ul :ul
            q :blockquote
            code :code
            :else :br))]
    (cond
      (re-matches #"[-_*]{3,}" (str/trim l))
      {:tag :hr :line ""}

      tag
      (u/maybe assoc
        {:spacing (count spacing)
         :line (if (= tag :br) l (str/trim remaining))
         :tag tag}
        :language code)

      (empty? (str/trim l)) {:tag :p :line l}

      :else {:line l})))

(defn group-annotated [lines]
  (->>
    (partition-by :tag lines)
    (mapcat
      (fn [group]
        (case (:tag (first group))
          :br [(into [:p]
                 (map-indexed
                   (fn [idx item]
                     (cond
                       (and (vector? item) (= :br (first item))) [:br {:key idx}]
                       (vector? item) (with-meta item {:key idx})
                       :else [:span {:key idx} item]))
                   (apply concat
                     (interpose [[:br]]
                       (map (fn [l] [(:line l)]) group)))))]
          :ul [(into [:ul] (map (fn [l] (into [:li] (:line l)))) group)]
          :ol [(into [:ol] (map (fn [l] (into [:li] (:line l))) group))]
          :hr [[:hr]]

          (map
            (fn [l]
              (into [(:tag l)]
                (map-indexed
                  (fn [idx item]
                    (cond
                      (vector? item) (with-meta item {:key idx})
                      :else [:span {:key idx} item])))
                (:line l)))
            group))))))


(defn parse [text]
  (let [result
        (->>
          (str/split text #"(?:\r\n)|(?:\r)|(?:\n)")
          (remove nil?)
          (map annotate-line-type)
          (map #(update % :line parse-line))
          (group-annotated))]
    (map-indexed
      (fn [idx item]
        (if (vector? item)
          (with-meta item {:key idx})
          item))
      result)))